import { datadogRum } from '@datadog/browser-rum';
import { matchPath } from 'react-router-dom';

import { redactSensitivePathname } from 'src/lib/analytics/redactSensitiveUrl';
import type { AnyRouteConfig } from 'src/lib/routeConfig/RouteConfig';
import { __registeredConfigs } from 'src/lib/routeConfig/RouteConfig';

/**
 * Resolves a raw pathname to its matching RouteConfig definition pattern.
 *
 * Iterates __registeredConfigs at call-time (not module-load-time) so that
 * lazy-loaded route chunks that register themselves later are included.
 * Returns the longest (most-specific) exact RouteConfig match, or `undefined`
 * if nothing matches.
 *
 * Configs used as `.extend()` arguments are skipped: their definitions are
 * suffixes, and `/:provider` (from `loginWithProvider`) would otherwise win
 * every single-segment path — `/login`, `/sandbox`, `/signup`, …
 */
export function getRouteDefinitionForPathname(
  pathname: string,
): AnyRouteConfig | undefined {
  try {
    let bestMatch: AnyRouteConfig | undefined;
    let bestLength = -1;

    for (const config of __registeredConfigs) {
      if (
        config.definition &&
        !config.isExtension &&
        config.definition.length > bestLength &&
        matchPath(pathname, { path: config.definition, exact: true })?.isExact
      ) {
        bestMatch = config;
        bestLength = config.definition.length;
      }
    }

    return bestMatch;
  } catch {
    return undefined;
  }
}

/**
 * Starts a new Datadog RUM view named by the matching route definition pattern.
 * Requires `trackViewsManually: true` in the RUM init config.
 * Never throws — analytics failures must never disrupt the user.
 */
export function startDatadogView(pathname: string): void {
  try {
    const match = getRouteDefinitionForPathname(pathname);
    datadogRum.startView({
      // When no route config matches we fall back to the raw pathname, which
      // for `/org/:orgId/invite/:joinToken` would put the join token in the
      // view name. Redact before it becomes a Datadog facet value (OE-1393).
      name: match?.definition ?? redactSensitivePathname(pathname),
      context: {
        pageSection: match?.pageSection,
      },
    });
  } catch {
    // intentionally swallowed
  }
}
